import { CREATABLE_TAG_FIELD } from 'constants/tags';
import {
  forbiddenCharacters,
  isEmpty,
  validateEmail,
  validateFieldsInList,
  validateFileType,
  validateLength,
  validateName,
  validatePhone,
} from '../../../utility/validation';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-dark.svg';
import { ReactComponent as ArchiveIcon } from '../../../assets/icons/archive.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-outlined.svg';
import { ReactComponent as DuplicateIcon } from '../../../assets/icons/content_copy.svg';
import {
  JOB_VIEW_OPTIONS,
  RECRUITMENT_STEPS_MAP,
  recruitmentStepsOptions,
  STEPS,
} from '../../../constants/job';
import { SINGLE_DAY_SAVE_FORMAT } from '../../../constants/date';
import { isArrayEmpty, trimString } from '../../../utility/helpers';
import { API_VALIDATE_CANDIDATES_EMAILS } from '../../../constants/apiRoutes';
import http from '../../../utility/http';
import { formatDate } from '../../../utility/dateUtils';
import {
  EDUCATION_FIELDS,
  NEW_EDUCATION_VALUES,
  NEW_EXPERIENCE_VALUES,
  EXPERIENCE_FIELDS,
} from '../../../constants/dateRangeWithText';
import { NEW_SKILL_VALUES, SKILL_FIELDS } from '../../../constants/skills';

const {
  TEXT,
  CHECKBOX,
  SUBTITLE,
  SELECT,
  DATE_PICKER,
  RADIO_GROUP,
  RICH_TEXT_EDITOR,
  DATE_RANGE_WITH_TEXT_LIST,
  TEXT_LIST,
  RECRUITMENT_STEPS,
} = FIELD_TYPES;

const TODAY = new Date();

export const getFields = (translations, classes) => {
  return [
    {
      name: 'titleSubtitle',
      type: SUBTITLE,
    },
    {
      name: 'title',
      type: TEXT,
      required: true,
      translationKey: 'title',
      shouldValidateInitialValue: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'inputMaxLength',
        },
      ],
    },
    {
      name: 'department',
      type: SELECT,
      optionsKey: 'departments',
      translationKey: 'department',
      parser: {
        value: 'id',
        label: 'name',
      },
    },
    {
      name: 'date_from',
      type: DATE_PICKER,
      translationKey: 'dateFrom',
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      getMaxDate: (value, values) => {
        return isEmpty(values.date_to) ? values.date_to : undefined;
      },
      required: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
      ],
    },
    {
      name: 'date_to',
      type: DATE_PICKER,
      translationKey: 'dateTo',
      valueFormat: SINGLE_DAY_SAVE_FORMAT,
      required: true,
      getMinDate: (value, values) => {
        return isEmpty(values.date_from) ? values.date_from : undefined;
      },
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
      ],
    },
    {
      name: 'workspaceSubtitle',
      type: SUBTITLE,
    },
    {
      name: 'workspace',
      type: RADIO_GROUP,
      customLabelClass: classes.customLabelClass,
      customLabelWrapperClass: classes.customLabelWrapperClass,
      customOptionLabelWrapperClass: classes.customOptionLabelWrapperClass,
      customRadioClass: classes.customRadioClass,
      options: [
        {
          value: 'on-site',
          label: translations.workspace.onSite.label,
          description: translations.workspace.onSite.description,
        },
        {
          value: 'hybrid',
          label: translations.workspace.hybrid.label,
          description: translations.workspace.hybrid.description,
        },
        {
          value: 'remote',
          label: translations.workspace.remote.label,
          description: translations.workspace.remote.description,
        },
      ],
    },
    {
      name: 'location',
      type: TEXT,
      translationKey: 'location',
      validators: [
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'inputMaxLength',
        },
      ],
    },
    {
      name: 'descriptionSubtitle',
      type: SUBTITLE,
      variant: 'h5',
    },
    {
      name: 'description',
      type: RICH_TEXT_EDITOR,
      shouldValidateInitialValue: true,
      multiline: true,
      rows: 5,
      validators: [
        {
          validator: fieldValue => validateLength(fieldValue, 0, 10000),
          type: 'maxLength',
        },
      ],
    },
    {
      name: 'requirements',
      type: RICH_TEXT_EDITOR,
      shouldValidateInitialValue: true,
      multiline: true,
      rows: 5,
      validators: [
        {
          validator: fieldValue => validateLength(fieldValue, 0, 10000),
          type: 'maxLength',
        },
      ],
    },
    {
      name: 'benefits',
      type: RICH_TEXT_EDITOR,
      shouldValidateInitialValue: true,
      multiline: true,
      rows: 5,
      validators: [
        {
          validator: fieldValue => validateLength(fieldValue, 0, 10000),
          type: 'maxLength',
        },
      ],
    },
    {
      name: 'tags',
      type: SELECT,
      optionsKey: 'categories',
      creatableOptionField: CREATABLE_TAG_FIELD,
      isCategorized: true,
      hasCategoryColorBox: true,
      isMulti: true,
      hasHash: true,
      isCreatable: true,
      isTag: true,
      shouldRemoveFieldSpacing: true,
      parser: {
        value: 'id',
        label: 'name',
        categoryLabel: 'name',
        categoryValue: 'tags',
      },
    },
  ];
};

export const prepareInitialData = job => ({
  title: job.title || '',
  department: job.department ? job.department.id : null,
  date_from: job.date_from || '',
  date_to: job.date_to || '',
  workspace: job.workspace || 'on-site',
  location: job.location || '',
  description: job.description || '',
  requirements: job.requirements || '',
  benefits: job.benefits || '',
  tags: job?.tags || [],
});

export const getDepartmentOptions = (
  departments,
  translations,
  hasAll = false
) => {
  if (hasAll) {
    return [{ id: 0, name: translations.all }, ...departments];
  }

  return departments;
};

export const prepareJobActionItems = (
  labels,
  job,
  canEdit,
  canDelete,
  onEdit,
  onDuplicate,
  onArchive,
  onDelete
) => [
  {
    type: 'edit',
    label: labels.edit,
    action: () => onEdit(job),
    shouldItemRender: () => canEdit,
    icon: EditIcon,
  },
  {
    type: 'duplicate',
    label: labels.duplicate,
    action: () => onDuplicate(job),
    shouldItemRender: () => canEdit,
    icon: DuplicateIcon,
  },
  {
    type: 'archive',
    label: labels.archive,
    action: () => onArchive(job),
    shouldItemRender: () => canEdit,
    icon: ArchiveIcon,
  },
  {
    type: 'delete',
    label: labels.delete,
    action: () => onDelete(job),
    shouldItemRender: () => canDelete,
    icon: DeleteIcon,
  },
];

export const getCandidateActionItems = (
  labels,
  job,
  onAddExistingCandidate,
  onAddCandidateManually,
  onUploadCv
) => [
  {
    type: 'upload_cv',
    label: labels.upload,
    action: () => onUploadCv(job),
    shouldItemRender: () => true,
  },
  {
    type: 'add_existing_candidate',
    label: labels.addExistingCandidate,
    action: () => onAddExistingCandidate(job),
    shouldItemRender: () => true,
  },
  {
    type: 'add_candidate',
    label: labels.addCandidate,
    action: () => onAddCandidateManually(job),
    shouldItemRender: () => true,
  },
];

export const prepareHiringTeamFields = classes => [
  {
    name: 'subtitle',
    type: FIELD_TYPES.SUBTITLE,
  },
  {
    name: 'hiring_team',
    type: FIELD_TYPES.PEOPLE_PICKER,
    className: classes.picker,
    fieldWrapperClass: classes.field,
    optionsKey: 'allUsers',
    translationKey: 'hiringTeam',
    showDirectReports: true,
    hasSelectAll: true,
    showSelection: true,
    shouldCalculateSelectionHeight: true,
  },
];

export const prepareHiringTeamInitialData = hiringTeam => ({
  hiring_team: hiringTeam,
});

export const prepareAddCandidatesFields = classes => [
  {
    name: 'subtitle',
    type: FIELD_TYPES.SUBTITLE,
  },
  {
    name: 'candidates',
    type: FIELD_TYPES.PEOPLE_PICKER,
    className: classes.picker,
    fieldWrapperClass: classes.field,
    optionsKey: 'allCandidates',
    translationKey: 'candidates',
    hasSelectAll: true,
    showSelection: true,
    shouldCalculateSelectionHeight: true,
  },
];

export const prepareCandidatesInitialData = candidatesForJob => ({
  candidates: candidatesForJob,
});

export const getViewOptions = labels =>
  JOB_VIEW_OPTIONS.map(view => ({
    ...view,
    name: labels[view.key],
  }));

export const getRecruitmentSteps = (jobId, steps = []) => {
  const hasSteps = !isArrayEmpty(steps);

  if (!hasSteps) {
    return {
      recruitment_steps: Object.values(RECRUITMENT_STEPS_MAP).map(value => {
        return {
          type: STEPS[value.title].id,
          title: value.title,
          hasCandidates: false,
          job: null,
        };
      }),
    };
  }

  return {
    recruitment_steps: steps.map(value => {
      return {
        ...value,
        type: value.type || STEPS[value.title]?.id,
        title: value.title,
        job: jobId,
        hasCandidates: Boolean(value?.number_of_candidates),
        id: value.id,
      };
    }),
  };
};

export const CANDIDATE_INFO = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  EMAIL: 'email',
  EDUCATION: 'education',
  EXPERIENCE: 'experience',
  SKILLS: 'skills',
  CV: 'cv',
  PARSE_CV_DATA: 'parse_cv_data',
  REFERRAL: 'referral',
  DATE_OF_BIRTH: 'date_of_birth',
  ADDRESS: 'address',
  CITY: 'city',
  COUNTRY: 'country',
  PHONE_NUMBER: 'phone',
  PROFESSION_TITLE: 'profession_title',
};

export const CANDIDATE_PROFILE_INFO = {
  EDUCATION: 'education',
  EXPERIENCE: 'experience',
  SKILLS: 'skills',
  CV: 'cv',
  DATE_OF_BIRTH: 'date_of_birth',
  CONTACT_DETAILS: 'contact_details',
  PROFESSION_TITLE: 'profession_title',
  REFERRAL: 'referral',
};

export const CONTACT_DETAILS_INFO = {
  ADDRESS: 'address',
  PHONE_NUMBER: 'phone',
  EMAIL: 'email',
};

export const ADDRESS_INFO = {
  ADDRESS: 'address',
  CITY: 'city',
  COUNTRY: 'country',
};

export const USER_VALIDATIONS = {
  [CANDIDATE_INFO.FIRST_NAME]: [
    {
      validator: isEmpty,
      type: 'required',
    },
    {
      validator: validateName,
      type: 'invalidName',
    },
    {
      validator: value => validateLength(value, 2, undefined, true),
      type: 'minLength',
    },
    {
      validator: value => validateLength(value, 0, 255),
      type: 'inputMaxLength',
    },
  ],
  [CANDIDATE_INFO.LAST_NAME]: [
    {
      validator: isEmpty,
      type: 'required',
    },
    {
      validator: validateName,
      type: 'invalidName',
    },
    {
      validator: value => validateLength(value, 2, undefined, true),
      type: 'minLength',
    },
    {
      validator: value => validateLength(value, 0, 255),
      type: 'inputMaxLength',
    },
  ],
  [CANDIDATE_INFO.EMAIL]: [
    {
      validator: value => !value || validateEmail(value),
      type: 'invalidEmail',
    },
    {
      validator: value => validateLength(value, 0, 100),
      type: 'exceededLengthEmail',
    },
    {
      type: 'availableEmail',
      async: true,
      shouldSkipValidation: true,
      validator: value => {
        const sanitizedUserEmail = trimString(value).toLowerCase();
        return isEmpty(sanitizedUserEmail)
          ? http
              .post(API_VALIDATE_CANDIDATES_EMAILS, {
                emails: [sanitizedUserEmail],
              })
              .then(({ data }) => !data?.taken?.includes(sanitizedUserEmail))
          : true;
      },
    },
  ],
  [CANDIDATE_INFO.REFERRAL]: [
    {
      validator: validateName,
      type: 'invalidName',
    },
    {
      validator: forbiddenCharacters,
      type: 'forbiddenCharacters',
    },
    {
      validator: value => validateLength(value, 0, 50),
      type: 'maxLength',
    },
  ],
  [CANDIDATE_INFO.PHONE_NUMBER]: [
    {
      validator: validatePhone,
      type: 'invalidPhone',
    },
  ],
  [CANDIDATE_INFO.PROFESSION_TITLE]: [
    {
      validator: forbiddenCharacters,
      type: 'forbiddenCharacters',
    },
    {
      validator: value => validateLength(value, 0, 255),
      type: 'inputMaxLength',
    },
  ],
  [CANDIDATE_INFO.SKILLS]: [
    {
      validator: skills => validateFieldsInList(skills, SKILL_FIELDS),
      isMultiple: true,
    },
  ],
  [CANDIDATE_INFO.EDUCATION]: [
    {
      validator: educations =>
        validateFieldsInList(educations, EDUCATION_FIELDS),
      isMultiple: true,
    },
  ],
  [CANDIDATE_INFO.EXPERIENCE]: [
    {
      validator: experiences =>
        validateFieldsInList(experiences, EXPERIENCE_FIELDS),
      isMultiple: true,
    },
  ],
  [CANDIDATE_INFO.CITY]: [
    {
      validator: forbiddenCharacters,
      type: 'forbiddenCharacters',
    },
    {
      validator: value => validateLength(value, 0, 50),
      type: 'maxLength',
    },
  ],
  [CANDIDATE_INFO.ADDRESS]: [
    {
      validator: forbiddenCharacters,
      type: 'forbiddenCharacters',
    },
    {
      validator: value => validateLength(value, 0, 255),
      type: 'inputMaxLength',
    },
  ],
  [CANDIDATE_INFO.COUNTRY]: [
    {
      validator: forbiddenCharacters,
      type: 'forbiddenCharacters',
    },
    {
      validator: value => validateLength(value, 0, 50),
      type: 'maxLength',
    },
  ],
  [CANDIDATE_INFO.CV]: [
    {
      type: 'fileType',
      validator: value => validateFileType(value, 'application/pdf'),
    },
    {
      validator: value => {
        return validateLength(value?.name, 0, 100);
      },
      type: 'fileLength',
    },
  ],
};

export const getCandidateFields = classes => [
  {
    name: CANDIDATE_INFO.FIRST_NAME,
    translationKey: 'firstName',
    fieldWrapperClass: classes.firstName,
    type: TEXT,
    required: true,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.FIRST_NAME]],
  },
  {
    name: CANDIDATE_INFO.LAST_NAME,
    translationKey: 'lastName',
    fieldWrapperClass: classes.lastName,
    type: TEXT,
    required: true,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.LAST_NAME]],
  },
  {
    name: CANDIDATE_INFO.EMAIL,
    translationKey: 'email',
    fieldWrapperClass: classes.email,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.EMAIL]],
  },
  {
    name: CANDIDATE_INFO.CV,
    type: FIELD_TYPES.UPLOAD_FILE,
    fieldWrapperClass: classes.cv,
    translationKey: 'cv',
    isMultiple: false,
    dependants: [{ name: CANDIDATE_INFO.PARSE_CV_DATA, value: false }],
    types: {
      'application/pdf': ['.pdf'],
    },
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.CV]],
  },
  {
    name: CANDIDATE_INFO.PARSE_CV_DATA,
    translationKey: 'parseCvData',
    fieldWrapperClass: classes.parseCheckbox,
    type: CHECKBOX,
    isDisabledCheck: values => !values.cv,
    isControlled: true,
  },
  {
    name: CANDIDATE_INFO.PROFESSION_TITLE,
    translationKey: 'professionTitle',
    fieldWrapperClass: classes.professionTitle,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.PROFESSION_TITLE]],
  },
  {
    name: CANDIDATE_INFO.EDUCATION,
    translationKey: 'education',
    fieldWrapperClass: classes.education,
    type: DATE_RANGE_WITH_TEXT_LIST,
    initialValues: NEW_EDUCATION_VALUES,
    fieldTypes: EDUCATION_FIELDS,
    isMultiple: true,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.EDUCATION]],
  },
  {
    name: CANDIDATE_INFO.EXPERIENCE,
    translationKey: 'experience',
    fieldWrapperClass: classes.experience,
    type: DATE_RANGE_WITH_TEXT_LIST,
    initialValues: NEW_EXPERIENCE_VALUES,
    fieldTypes: EXPERIENCE_FIELDS,
    isMultiple: true,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.EXPERIENCE]],
  },
  {
    name: CANDIDATE_INFO.SKILLS,
    translationKey: 'skills',
    fieldWrapperClass: classes.skills,
    type: TEXT_LIST,
    initialValues: NEW_SKILL_VALUES,
    fieldTypes: SKILL_FIELDS,
    isMultiple: true,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.SKILLS]],
  },
  {
    name: CANDIDATE_INFO.REFERRAL,
    translationKey: 'referral',
    fieldWrapperClass: classes.referral,
    type: TEXT,
    required: true,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.REFERRAL]],
  },
  {
    name: CANDIDATE_INFO.DATE_OF_BIRTH,
    translationKey: 'dateOfBirth',
    fieldWrapperClass: classes.dateOfBirth,
    type: DATE_PICKER,
    getMaxDate: () =>
      new Date(
        +formatDate(TODAY, 'yyyy') - 18,
        TODAY.getMonth(),
        TODAY.getDate()
      ),
    isDisabledCheck: values => values.parse_cv_data,
    valueFormat: SINGLE_DAY_SAVE_FORMAT,
  },
  {
    name: CANDIDATE_INFO.PHONE_NUMBER,
    translationKey: 'phone',
    fieldWrapperClass: classes.phone,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.PHONE_NUMBER]],
  },
  {
    name: CANDIDATE_INFO.ADDRESS,
    translationKey: 'address',
    fieldWrapperClass: classes.address,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.ADDRESS]],
  },
  {
    name: CANDIDATE_INFO.CITY,
    translationKey: 'city',
    fieldWrapperClass: classes.city,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.CITY]],
  },
  {
    name: CANDIDATE_INFO.COUNTRY,
    translationKey: 'country',
    fieldWrapperClass: classes.country,
    type: TEXT,
    isDisabledCheck: values => values.parse_cv_data,
    shouldRemoveFieldSpacing: true,
    shouldRemoveFieldError: true,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.COUNTRY]],
  },
];

export const prepareCandidateInitialData = (candidate = {}) => ({
  id: candidate.id || null,
  [CANDIDATE_INFO.FIRST_NAME]: candidate.first_name || '',
  [CANDIDATE_INFO.LAST_NAME]: candidate.last_name || '',
  [CANDIDATE_INFO.EMAIL]: candidate.email || '',
  [CANDIDATE_INFO.EDUCATION]: candidate.education || [],
  [CANDIDATE_INFO.EXPERIENCE]: candidate.experience || [],
  [CANDIDATE_INFO.SKILLS]: candidate.skills || [],
  [CANDIDATE_INFO.REFERRAL]: candidate.referral || '',
  [CANDIDATE_INFO.DATE_OF_BIRTH]: candidate.date_of_birth || null,
  [CANDIDATE_INFO.CV]: candidate.cv || '',
  [CANDIDATE_INFO.PARSE_CV_DATA]: candidate.ongoing_cv_parsing,
  [CANDIDATE_INFO.ADDRESS]: candidate.address || '',
  [CANDIDATE_INFO.CITY]: candidate.city || '',
  [CANDIDATE_INFO.COUNTRY]: candidate.country || '',
  [CANDIDATE_INFO.PHONE_NUMBER]: candidate.phone || '',
  [CANDIDATE_INFO.PROFESSION_TITLE]: candidate.profession_title || '',
});

export const STEPS_FIELDS = [
  {
    name: 'type',
    type: FIELD_TYPES.SELECT,
    translationKey: 'type',
    required: true,
    validators: [
      {
        validator: isEmpty,
        type: 'requiredStep',
      },
    ],
  },
  {
    name: 'title',
    type: FIELD_TYPES.TEXT,
    translationKey: 'title',
    required: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: value => validateLength(value, 0, 25),
        type: 'maxLength',
      },
    ],
  },
];

export const UPLOAD_CV_INITIAL_DATA = {
  [CANDIDATE_INFO.FIRST_NAME]: '',
  [CANDIDATE_INFO.LAST_NAME]: '',
  [CANDIDATE_INFO.CV]: '',
};

export const getUploadCvFields = () => [
  {
    name: CANDIDATE_INFO.FIRST_NAME,
    translationKey: 'firstName',
    type: TEXT,
    required: true,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.FIRST_NAME]],
  },
  {
    name: CANDIDATE_INFO.LAST_NAME,
    translationKey: 'lastName',
    type: TEXT,
    required: true,
    validators: [...USER_VALIDATIONS[CANDIDATE_INFO.LAST_NAME]],
  },
  {
    name: CANDIDATE_INFO.CV,
    type: FIELD_TYPES.UPLOAD_FILE,
    translationKey: 'cv',
    isMultiple: false,
    required: true,
    types: {
      'application/pdf': ['.pdf'],
    },
    validators: [
      ...USER_VALIDATIONS[CANDIDATE_INFO.CV],
      {
        validator: isEmpty,
        type: 'required',
      },
    ],
  },
];

export const NEW_STEPS_VALUES = {
  type: RECRUITMENT_STEPS_MAP.SOURCED.id,
  title: RECRUITMENT_STEPS_MAP.SOURCED.title,
};

export const GET_STEPS_FIELDS = [
  {
    name: 'recruitment_steps',
    translationKey: 'step',
    type: RECRUITMENT_STEPS,
    initialSteps: NEW_STEPS_VALUES,
    fieldTypes: STEPS_FIELDS,
    options: recruitmentStepsOptions(),
    isMultiple: true,
    shouldRemoveFieldSpacing: true,
    shouldRemoveFieldError: true,
    validators: [
      {
        validator: steps => validateFieldsInList(steps, STEPS_FIELDS),
        isMultiple: true,
      },
    ],
  },
];
